import cx from "classnames";

import {
  ComponentType,
  FC,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IonButton,
  IonInputOtp,
  IonLabel,
  useIonRouter,
  useIonViewWillLeave,
} from "@ionic/react";

import s from "./WLPhoneVerification.module.scss";
import { WLButton } from "../../../Common/components/WLButton/WLButton";
import { useMutation } from "@tanstack/react-query";
import { useCommonStore } from "../../CommonStores/phoneVerificationStore";
import { sendSMSVerification } from "../../api/otp";
import { useFormsStore } from "../../../Common/store/common";
import {
  checkIfCanVerifyMobile,
  formatSecondsWithMinutes,
  getErrorMsg,
  setRecentMobileVerification,
} from "../../../Common/utils/common";
import { bugNotif } from "../../api/bugsnag";
import { isEmpty } from "lodash";
import WLToastError from "../../components/WLToastError/WLToastError";
import { verifyWebOtpLogin } from "../../api/user";

interface WLPhoneVerificationProps {
  userId: string;
  phoneNumber: string;
  title: string;
  onVerifySuccess: () => void;
  removeTermsAndConditions?: boolean;
  /** Plain copy when `descriptionComponent` is not used */
  description?: string;
  /** Renders instead of `description` when provided */
  descriptionComponent?: ComponentType;
  /** Optional block below the OTP area (e.g. terms, extra context) */
  bottomDetailsComponent?: ComponentType;
}

const WLPhoneVerification: FC<WLPhoneVerificationProps> = ({
  userId,
  phoneNumber,
  title,
  onVerifySuccess,
  description,
  descriptionComponent: DescriptionComponent,
  bottomDetailsComponent: BottomDetailsComponent,
}) => {
  const router = useIonRouter();

  const [showToastError, setShowToastError] = useState(false);
  const [error, setError] = useState("");

  const { formLoading, setFormLoading } = useFormsStore();

  const { resendCoolDown, setResendCoolDown } = useCommonStore();

  const [otpCode, setOtpCode] = useState<string>("");

  const inputOtpCodeRef = useRef<HTMLIonInputOtpElement>(null);
  const hasRequested = useRef(false);

  const { mutate: onSendOTP } = useMutation({
    mutationFn: sendSMSVerification,
    onMutate: () => {
      console.log("will send from set phone");
    },
    onSuccess: () => {
      setRecentMobileVerification();
      setFormLoading(false);
    },
    onError: (err: any) => {
      const { errorMsg, rawErrorMsg } = getErrorMsg(err);
      setFormLoading(false);
      setError(errorMsg);
      setShowToastError(true);
      bugNotif("onSendOTP - SetPhoneNumber", rawErrorMsg);
    },
  });

  const { mutate: onResendOTP } = useMutation({
    mutationFn: sendSMSVerification,
    onMutate: () => {
      console.log("will send from confirm phone");
      setOtpCode("");
      setError("");
      setShowToastError(false);
    },
    onSuccess: (response) => {
      setRecentMobileVerification();
      checkIfCanVerifyMobile((remainingTime) => {
        setResendCoolDown(~~remainingTime);
      });
      console.log("got response on otp", response);
    },
    onError: (err: any) => {
      console.log("error resnd -- ", err);
      const { errorMsg, rawErrorMsg } = getErrorMsg(err);
      setError(errorMsg);
      setShowToastError(true);
      bugNotif("onResendOTP - ConfirmPhoneNumber", rawErrorMsg);
    },
  });

  const {
    mutate: onVerifyOtp,
    isSuccess: isVerifySuccess,
    isPending: isVerifyLoading,
  } = useMutation({
    mutationFn: verifyWebOtpLogin,
    onMutate: () => {
      setFormLoading(true);
    },
    onSuccess: async (response) => {
      const valid = response.data.valid;
      console.log("Success - response verified -- ", response);
      setFormLoading(false);
      if (!valid) {
        setError("Wrong code, please try again.");
        setShowToastError(true);
      } else {
        console.log("setting form loading to false onVerifyOtp");
        setError("");
        setShowToastError(false);
        onVerifySuccess();
      }
    },
    onError: (err: any) => {
      console.log("setting form loading to false on error - onVerifyOtp");
      setFormLoading(false);
      const { errorMsg, rawErrorMsg } = getErrorMsg(err);
      console.log("error -- ", errorMsg, rawErrorMsg);
      setError(errorMsg);
      setShowToastError(true);
      bugNotif("signUpPhoneNumberVerify", rawErrorMsg);
    },
  });

  const submitOTPCode = () => {
    if (!isEmpty(otpCode) && otpCode.length === 4) {
      onVerifyOtp({
        userId,
        phoneNumber,
        code: otpCode,
      });
    }
  };

  useEffect(() => {
    if (inputOtpCodeRef.current) {
      inputOtpCodeRef.current.setFocus();
    }
  }, [inputOtpCodeRef.current]);

  useEffect(() => {
    submitOTPCode();
  }, [otpCode]);

  const cleanup = () => {
    setOtpCode("");
  };

  useIonViewWillLeave(() => {
    cleanup();
    console.log("setting form loading to false on will leave");
    setFormLoading(false);
  });

  useEffect(() => {
    if (!resendCoolDown && phoneNumber && !hasRequested.current) {
      hasRequested.current = true;
      onSendOTP(phoneNumber);
      checkIfCanVerifyMobile((remainingTime) => {
        setResendCoolDown(~~remainingTime);
      });
    }
  }, [resendCoolDown, phoneNumber]);

  const getOtpInputClassName = useMemo(() => {
    return cx(s.otpInput, {
      [s.otpInputError]: !isVerifyLoading && !isEmpty(error),
      [s.otpInputSuccess]: isVerifySuccess,
    });
  }, [error, isVerifyLoading, isVerifySuccess]);

  return (
    <div className={s.container}>
      <IonLabel className="win-header-3 extra-bold winnersans secondary-400 ion-text-center">
        {title}
      </IonLabel>

      <div className={s.descriptionContainer}>
        {DescriptionComponent ? (
          <DescriptionComponent />
        ) : (
          description && (
            <IonLabel className="win-body regular gilroy white">
              {description}
            </IonLabel>
          )
        )}

        <IonLabel className="win-body regular gilroy white">
          {phoneNumber}
        </IonLabel>
      </div>

      <div className={s.formContainer}>

        <div className={s.otpInputs}>
          <IonInputOtp
            ref={inputOtpCodeRef}
            className={getOtpInputClassName}
            length={4}
            value={otpCode}
            type="number"
            onIonInput={(e) => {
              const value = e.detail.value || "";
              setOtpCode(value);
              setError("");
              setShowToastError(false);
            }}
          ></IonInputOtp>
          {!isVerifyLoading && !isEmpty(error) && (
            <IonLabel
              className={cx(
                s.otpMessage,
                s.error,
                "win-sub medium gilroy danger ion-padding-top"
              )}
            >
              {error}
            </IonLabel>
          )}
          <div className={s.otpMessageContainer}>
            <IonLabel className="win-sub regular gilroy white">
              Didn't receive any code?{" "}
              {resendCoolDown ? <>Resend in&nbsp;</> : <></>}
            </IonLabel>
            {resendCoolDown ? (
              <>
                <IonLabel className="win-sub semi-bold gilroy secondary-500">
                  {formatSecondsWithMinutes(resendCoolDown || 30)}
                </IonLabel>
              </>
            ) : (
              <>
                &nbsp;
                <IonButton
                  fill="clear"
                  size="small"
                  className={cx(
                    s.resendButton,
                    "win-sub bold gilroy ion-no-padding ion-no-margin"
                  )}
                  disabled={formLoading}
                  onClick={() => {
                    onResendOTP(phoneNumber);
                  }}
                >
                  Resend
                </IonButton>
              </>
            )}
          </div>
        </div>
      </div>

      {BottomDetailsComponent ? <BottomDetailsComponent /> : null}

      <div className={s.footer}>
        <WLButton
          onClick={submitOTPCode}
          className={cx(
            s.button,
            "win-sub medium gilroy red-submit-button ion-no-margin"
          )}
          disabled={isEmpty(otpCode) || otpCode.length !== 4}
          isFormComponent
        >
          Verify
        </WLButton>
      </div>

      <WLToastError
        isOpen={!isEmpty(error) && showToastError}
        duration={3000}
        message={error}
        onDidDismiss={() => {
          setShowToastError(false);
        }}
      />
    </div>
  );
};

export default memo(WLPhoneVerification);
